import { HitokotoQuote, ProcessedQuote, ApiError } from '@/types';
import { config } from '@/config';

class HitokotoService {
  private readonly retryCount = 3;
  private readonly retryDelay = 1000;

  async getQuote(): Promise<ProcessedQuote> {
    try {
      const quote = await this.fetchQuote();
      return this.formatQuoteData(quote);
    } catch (error) {
      console.error('获取一言失败:', error);
      
      // 尝试fallback URLs
      if (config.hitokoto.fallbackUrls) {
        for (const fallbackUrl of config.hitokoto.fallbackUrls) {
          try {
            console.log('尝试备用API:', fallbackUrl);
            const quote = await this.fetchQuoteFromUrl(fallbackUrl);
            return this.formatQuoteData(quote);
          } catch (fallbackError) {
            console.warn('备用API也失败:', fallbackError);
          }
        }
      }
      
      return this.getFallbackQuote();
    }
  }

  private async fetchQuoteFromUrl(baseUrl: string): Promise<HitokotoQuote> {
    // 随机选择类型
    const types = config.hitokoto.types;
    const type = types[Math.floor(Math.random() * types.length)];
    
    const params = new URLSearchParams({
      c: type
    });
    
    const url = `${baseUrl}?${params}`;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5秒超时（备用API使用更短超时）
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'DailyQuoteWebsite/2.0'
        },
        mode: 'cors',
        cache: 'no-cache',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new ApiError(
          `一言API请求失败: ${response.status} ${response.statusText}`,
          response.status,
          'hitokoto'
        );
      }
      
      const data = await response.json();
      return data as HitokotoQuote;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  private async fetchQuote(attempt = 1): Promise<HitokotoQuote> {
    try {
      return await this.fetchQuoteFromUrl(config.hitokoto.baseUrl);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      // 检查是否是网络连接问题
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        console.error('一言API网络连接失败，可能是CORS问题或网络不可达:', error);
        
        if (attempt < this.retryCount) {
          console.warn(`网络错误，${this.retryDelay}ms后重试... (尝试 ${attempt}/${this.retryCount})`);
          await this.sleep(this.retryDelay * attempt);
          return await this.fetchQuote(attempt + 1);
        }
        
        throw new ApiError('网络连接失败，请检查网络连接', 0, 'hitokoto');
      }
      
      // 检查是否是AbortError（超时）
      if (error instanceof Error && error.name === 'AbortError') {
        console.error('一言API请求超时:', error);
        
        if (attempt < this.retryCount) {
          console.warn(`请求超时，${this.retryDelay}ms后重试... (尝试 ${attempt}/${this.retryCount})`);
          await this.sleep(this.retryDelay * attempt);
          return await this.fetchQuote(attempt + 1);
        }
        
        throw new ApiError('请求超时，请稍后再试', 0, 'hitokoto');
      }
      
      console.error('一言API未知错误:', error);
      throw new ApiError('获取名言失败', 0, 'hitokoto');
    }
  }

  private formatQuoteData(quote: HitokotoQuote): ProcessedQuote {
    let source = quote.from || '未知出处';
    
    // 如果有作者信息，添加到来源中
    if (quote.from_who) {
      source = `${quote.from_who}，${source}`;
    } else if (source && !source.includes('，')) {
      // 如果来源不包含逗号，可能需要添加"作者"前缀
      // 但一言API的数据通常已经格式化好了
      source = `出处，${source}`;
    }
    
    return {
      text: quote.hitokoto,
      source: source,
      uuid: quote.uuid
    };
  }

  private getFallbackQuote(): ProcessedQuote {
    const fallbackQuotes = [
      {
        text: '山重水复疑无路，柳暗花明又一村。',
        source: '陆游，《游山西村》'
      },
      {
        text: '海上生明月，天涯共此时。',
        source: '张九龄，《望月怀远》'
      },
      {
        text: '春风得意马蹄疾，一日看尽长安花。',
        source: '孟郊，《登科后》'
      },
      {
        text: '落红不是无情物，化作春泥更护花。',
        source: '龚自珍，《己亥杂诗》'
      },
      {
        text: '人生如逆旅，我亦是行人。',
        source: '苏轼，《临江仙·送钱穆父》'
      }
    ];
    
    return fallbackQuotes[Math.floor(Math.random() * fallbackQuotes.length)];
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const hitokotoService = new HitokotoService();